import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CheckCircle2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState({ payment_status: "pending" });
  const [attempts, setAttempts] = useState(0);
  const nav = useNavigate();
  const { refreshBalance } = useAuth();

  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    const poll = async () => {
      for (let i = 0; i < 15 && !cancelled; i++) {
        try {
          const { data } = await api.get(`/payments/status/${sessionId}`);
          if (!cancelled) {
            setStatus(data);
            setAttempts(i + 1);
            if (data.payment_status === "paid" || data.payment_status === "failed") {
              await refreshBalance();
              return;
            }
          }
        } catch (e) { /* transient — retry */ }
        await new Promise((r) => setTimeout(r, 2000));
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId, refreshBalance]);

  const paid = status.payment_status === "paid";
  const failed = status.payment_status === "failed";

  return (
    <div className="p-12 flex items-center justify-center min-h-[70vh]">
      <div className="max-w-md text-center">
        {paid ? (
          <>
            <CheckCircle2 className="w-20 h-20 text-emerald-400 mx-auto mb-6" strokeWidth={1.5} />
            <div className="font-mono-tech text-[10px] text-emerald-400 mb-3">PAYMENT · SUCCESSFUL</div>
            <h1 className="font-heading text-4xl font-bold tracking-tighter mb-3">
              +{status.credits_granted} credits
            </h1>
            <p className="text-zinc-400 mb-6">
              Your balance has been updated. Ready to create some cinematic listings?
            </p>
            <div className="flex flex-col gap-2">
              <Button onClick={() => nav("/projects/new")} data-testid="cta-new-project"
                className="bg-amber-500 hover:bg-amber-600 text-black font-semibold h-11">
                Create a new video
              </Button>
              <Button variant="ghost" onClick={() => nav("/")}
                className="text-zinc-400 hover:text-white">Back to dashboard</Button>
            </div>
          </>
        ) : failed ? (
          <>
            <div className="font-mono-tech text-[10px] text-red-400 mb-3">PAYMENT · FAILED</div>
            <h1 className="font-heading text-3xl font-bold tracking-tight mb-3">Something went wrong</h1>
            <p className="text-zinc-400 mb-6">Please try again or contact support.</p>
            <Button onClick={() => nav("/pricing")} className="bg-amber-500 hover:bg-amber-600 text-black font-semibold">
              Try again
            </Button>
          </>
        ) : (
          <>
            <Loader2 className="w-12 h-12 text-amber-400 mx-auto mb-6 animate-spin" strokeWidth={1.5} />
            <div className="font-mono-tech text-[10px] text-amber-400 mb-3">CONFIRMING · PAYMENT</div>
            <h1 className="font-heading text-3xl font-bold tracking-tight mb-3">Almost there…</h1>
            <p className="text-zinc-400">Verifying your payment (attempt {attempts}/15).</p>
          </>
        )}
      </div>
    </div>
  );
}
